<app-closeable-dialog-title
  (maximizedChanged)="toggleMaximize($event)"
  title="User Settings">
</app-closeable-dialog-title>

<div mat-dialog-content class="maximize dialog--content">
  <h3>
    LLM-Model Settings
  </h3>

  <p>Chose your favorite LLM which will support you in conducting your systematic review</p>

  @if (!isProduction) {
    @if (deviceId) {
      <p><strong>DeviceId: </strong>{{ deviceId }}</p>
    }
  }
  @if (isLocalDevelopment) {
    <button mat-raised-button (click)="setDeviceIdToTest()">(DEV) Use test user</button>
  }
  <p>Select your favorite LLM provider, and select your favorite model</p>
  <form [formGroup]="form" (ngSubmit)="onConfirm()">
    <mat-radio-group formControlName="selectedLLM" class="full-width">
      <mat-radio-button [value]="LlmType.OLLAMA">Ollama</mat-radio-button>
      <mat-radio-button [value]="LlmType.CHATGPT">ChatGPT</mat-radio-button>
      <mat-radio-button [value]="LlmType.GEMINI">Google AI Gemini</mat-radio-button>
    </mat-radio-group>

    @if (isOllamaEnabled()) {
      <mat-form-field class="full-width">
        <mat-label>Chat Model</mat-label>
        <mat-select formControlName="ollamaModel">
          @for (ollamaModel of ollamaModels; track $index) {
            <mat-option [value]="ollamaModel.value">{{ ollamaModel.viewValue }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
      <mat-form-field class="full-width">
        <mat-label>Embedding Model</mat-label>
        <mat-select formControlName="ollamaEmbeddingModel">
          @for (ollamaModel of ollamaEmbeddingModels; track $index) {
            <mat-option [value]="ollamaModel.value">{{ ollamaModel.viewValue }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
      <mat-form-field class="full-width">
        <mat-label>Ollama Base URL</mat-label>
        <input matInput formControlName="ollamaBaseUrl"/>
        <mat-hint>Only if you would like to use a custom one</mat-hint>
      </mat-form-field>
    }
    @if (isChatGptEnabled()) {
      <mat-form-field class="full-width">
        <mat-label>Chat Model</mat-label>
        <mat-select formControlName="chatGptModel">
          @for (chatGPTModel of chatGPTModels; track $index) {
            <mat-option [value]="chatGPTModel.value">{{ chatGPTModel.viewValue }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
      <mat-form-field class="full-width">
        <mat-label>OpenAI API Key</mat-label>
        <input matInput formControlName="chatGptApiKey"/>
      </mat-form-field>
      <mat-form-field class="full-width">
        <mat-label>Embedding Model</mat-label>
        <mat-select formControlName="chatGptEmbeddingModel">
          @for (chatGPTModel of chatGPTEmbeddingModels; track $index) {
            <mat-option [value]="chatGPTModel.value">{{ chatGPTModel.viewValue }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
    }
    @if (isGeminiEnabled()) {
      <mat-form-field class="full-width">
        <mat-label>Chat Model</mat-label>
        <mat-select formControlName="geminiModel">
          @for (geminiModel of geminiGPTModels; track $index) {
            <mat-option [value]="geminiModel.value">{{ geminiModel.viewValue }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
      <mat-form-field class="full-width">
        <mat-label>Google AI Gemini API Key</mat-label>
        <input matInput formControlName="geminiApiKey"/>
      </mat-form-field>
      <mat-form-field class="full-width">
        <mat-label>Embedding Model</mat-label>
        <mat-select formControlName="geminiEmbeddingModel">
          @for (geminiModel of geminiGPTEmbeddingModels; track $index) {
            <mat-option [value]="geminiModel.value">{{ geminiModel.viewValue }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
    }

    <mat-divider></mat-divider>
    <mat-accordion>
      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title>Advanced LLM Settings</mat-panel-title>
        </mat-expansion-panel-header>

        <div class="advanced-settings">
          <mat-form-field class="full-width">
            <mat-label>Temperature</mat-label>
            <input matInput type="number" step="0.1" formControlName="temperature">
            <mat-hint>Controls randomness (0.0-2.0). Lower values (0.0-0.3) for factual responses, higher values
              (0.7-1.0) for creative ones.
            </mat-hint>
            @if (form.get('temperature')?.invalid) {
              <mat-error>Value must be between 0 and 2</mat-error>
            }
          </mat-form-field>

          <mat-form-field class="full-width">
            <mat-label>Top-K</mat-label>
            <input matInput type="number" formControlName="topK">
            <mat-hint>Limits token selection to the K most likely tokens. Lower values produce more focused output, 0
              disables.
            </mat-hint>
            @if (form.get('topK')?.invalid) {
              <mat-error>Value must be 0 or greater</mat-error>
            }
          </mat-form-field>

          <mat-form-field class="full-width">
            <mat-label>Top-P</mat-label>
            <input matInput type="number" step="0.05" formControlName="topP">
            <mat-hint>Nucleus sampling parameter (0.0-1.0). Values between 0.7-0.95 typically work well.</mat-hint>
            @if (form.get('topP')?.invalid) {
              <mat-error>Value must be between 0 and 1</mat-error>
            }
          </mat-form-field>

          <mat-form-field class="full-width">
            <mat-label>Repeat Penalty</mat-label>
            <input matInput type="number" step="0.1" formControlName="repeatPenalty">
            <mat-hint>Penalizes repetition of tokens. Higher values (>1.0) reduce repetitive text. Values 1.1-1.3 often
              work best.
            </mat-hint>
            @if (form.get('repeatPenalty')?.invalid) {
              <mat-error>Value must be 1.0 or greater</mat-error>
            }
          </mat-form-field>

          <mat-form-field class="full-width">
            <mat-label>Seed</mat-label>
            <input matInput type="number" formControlName="seed">
            <mat-hint>Random seed for deterministic generation. Use specific value for reproducible results, 0 for
              random.
            </mat-hint>
          </mat-form-field>

          <mat-form-field class="full-width">
            <mat-label>Max Tokens (numPredict)</mat-label>
            <input matInput type="number" formControlName="numPredict">
            <mat-hint>Maximum number of tokens to generate in the response. Typical range: 128-2048.</mat-hint>
            @if (form.get('numPredict')?.invalid) {
              <mat-error>Value must be between -1 and 8192</mat-error>
            }
          </mat-form-field>

          <mat-form-field class="full-width">
            <mat-label>Context Size (numCtx)</mat-label>
            <input matInput type="number" formControlName="numCtx">
            <mat-hint>Size of the context window. Depends on model, typically 8192-{{currentMaxTokens}} tokens.</mat-hint>
            @if (form.get('numCtx')?.invalid) {
              <mat-error>Value must be between 1 and {{currentMaxTokens}}</mat-error>
            }
          </mat-form-field>
        </div>
      </mat-expansion-panel>
    </mat-accordion>

    <mat-divider></mat-divider>
    <h3>
      Query Settings
    </h3>

    <mat-form-field class="full-width">
      <mat-label>PubMed API Key</mat-label>
      <input matInput formControlName="pubMedApiKey"/>
      <mat-hint>If you don’t have an API key, E-utilities will still work, but you may be limited to fewer requests than
        allowed with an API key.<a target="_blank" href="https://account.ncbi.nlm.nih.gov/">Get your key</a></mat-hint>
    </mat-form-field>

    <mat-form-field class="full-width">
      <mat-label>Scopus API Key</mat-label>
      <input matInput formControlName="scopusApiKey"/>
      <mat-hint><a target="_blank" href="https://dev.elsevier.com/">Get your key</a></mat-hint>
    </mat-form-field>

    <mat-form-field class="full-width">
      <mat-label>Semantic Scholar API Key</mat-label>
      <input matInput formControlName="semanticScholarApiKey"/>
      <mat-hint>Most Semantic Scholar endpoints are available to the public without authentication, but they are
        rate-limited to 1000 requests per second shared among all unauthenticated users. Requests may also be further
        throttled during periods of heavy use. <a target="_blank"
                                                  href="https://www.semanticscholar.org/product/api#api-key/">Get your
          key</a></mat-hint>
    </mat-form-field>
  </form>

  @if (needsEmbeddingClear()) {
    <div class="warning">
      <mat-icon>warning</mat-icon>
      <span>All textembeddings will be deleted, you need perform a new screening</span>
      <mat-icon>warning</mat-icon>
    </div>
  }
</div>


<div mat-dialog-actions class="actions">
  <button mat-raised-button (click)="clearSetting()">
    <mat-icon>clear</mat-icon>
    Default
  </button>

  <button mat-raised-button
          class="delete-btn"
          (click)="deleteSettings()">
    <mat-icon>delete</mat-icon>
    Delete
  </button>

  <span class="spacer"></span>
  <button mat-raised-button
          class="save-btn"
          [disabled]="form.invalid"
          (click)="onConfirm()">
    <mat-icon>save</mat-icon>
    Save
  </button>

</div>
